import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '@/shared/ui/atoms';
import { howtoApi } from '@/features/howto/api';
import { HowtoEditor } from '@/features/howto/editor/HowtoEditor';
import type { HowtoDoc, HowtoDocSummary } from '@/features/howto/types';

const UNCATEGORIZED = '분류 없음';
const SAVE_DELAY_MS = 800;
const EMPTY_BODY = JSON.stringify({ type: 'doc', content: [{ type: 'paragraph' }] });

type SaveState = 'saved' | 'dirty' | 'saving' | 'failed';

const SAVE_LABEL: Record<SaveState, string> = { saved: '저장됨', dirty: '수정 중…', saving: '저장 중…', failed: '저장 실패' };

/** 분류별로 묶는다. 분류 없는 문서는 맨 뒤다. */
function groupByCategory(docs: HowtoDocSummary[]): [string, HowtoDocSummary[]][] {
  const groups = new Map<string, HowtoDocSummary[]>();
  for (const doc of docs) groups.set(doc.category ?? UNCATEGORIZED, [...(groups.get(doc.category ?? UNCATEGORIZED) ?? []), doc]);
  return [...groups.entries()].sort(([a], [b]) => (a === UNCATEGORIZED ? 1 : b === UNCATEGORIZED ? -1 : a.localeCompare(b, 'ko')));
}

/**
 * how to — 위키처럼 읽고 노션처럼 그 자리에서 고친다. 문서를 열면 바로 편집 상태이고, 손을 멈추면 자동 저장된다.
 * 열린 문서는 주소(`?doc=`)에 실려 새로 고침·링크 공유로 같은 문서가 열린다.
 */
export function HowtoView(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const openId = Number(params.get('doc')) || null;
  const [docs, setDocs] = useState<HowtoDocSummary[] | null>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const loadList = useCallback(() => {
    howtoApi
      .list()
      .then((payload) => setDocs(payload.docs))
      .catch((cause: Error) => setError(cause.message));
  }, []);

  useEffect(loadList, [loadList]);

  const open = useCallback((id: number | null) => setParams(id ? { doc: String(id) } : {}), [setParams]);

  const create = useCallback(() => {
    howtoApi
      .create({ title: '제목 없음', category: null, body: EMPTY_BODY })
      .then((doc) => {
        loadList();
        open(doc.id);
      })
      .catch((cause: Error) => setError(cause.message));
  }, [loadList, open]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (docs ?? []).filter((doc) => needle === '' || `${doc.title} ${doc.category ?? ''}`.toLowerCase().includes(needle));
  }, [docs, query]);

  const categories = useMemo(() => [...new Set((docs ?? []).flatMap((doc) => (doc.category ? [doc.category] : [])))], [docs]);

  return (
    <div className="flex min-h-[calc(100vh-var(--spacing-topbar)-2rem)] gap-4">
      <aside className="flex w-64 shrink-0 flex-col gap-2">
        <div className="flex gap-2">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="문서 검색"
            className="min-w-0 flex-1 rounded-md border border-line px-2.5 py-1.5 text-[13px]"
          />
          <button
            type="button"
            onClick={create}
            className="cursor-pointer rounded-md bg-accent px-2.5 py-1.5 text-[12.5px] font-semibold text-white"
          >
            + 새 문서
          </button>
        </div>
        <nav className="flex flex-col gap-3 overflow-y-auto">
          {docs === null && <span className="px-1 text-[12px] text-muted">불러오는 중…</span>}
          {docs !== null && visible.length === 0 && <span className="px-1 text-[12px] text-muted">문서가 없다</span>}
          {groupByCategory(visible).map(([category, items]) => (
            <div key={category}>
              <div className="px-1 pb-1 text-[11px] font-semibold text-muted">{category}</div>
              {items.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => open(doc.id)}
                  className={`block w-full cursor-pointer truncate rounded-md px-2 py-1 text-left text-[13px] ${doc.id === openId ? 'bg-accent-soft font-semibold' : 'hover:bg-line-soft'}`}
                >
                  {doc.title}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <section className="min-w-0 flex-1 rounded-lg border border-line bg-surface px-10 py-8">
        {error && (
          <div role="alert" className="mb-3 flex items-start justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-800">
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} className="cursor-pointer">닫기</button>
          </div>
        )}
        {openId === null ? (
          <EmptyState message="왼쪽에서 문서를 고르거나 새 문서를 만든다" />
        ) : (
          <DocPage
            key={openId}
            id={openId}
            categories={categories}
            onError={setError}
            onSaved={loadList}
            onRemoved={() => {
              loadList();
              open(null);
            }}
          />
        )}
      </section>
    </div>
  );
}

/** 문서 한 장 — 제목·분류·본문을 한 번에 자동 저장한다. */
function DocPage({
  id, categories, onError, onSaved, onRemoved,
}: {
  id: number;
  categories: string[];
  onError: (message: string) => void;
  onSaved: () => void;
  onRemoved: () => void;
}): React.JSX.Element {
  const [doc, setDoc] = useState<HowtoDoc | null>(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [state, setState] = useState<SaveState>('saved');
  const latest = useRef<{ title: string; category: string; body: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saving = useRef(false);

  useEffect(() => {
    howtoApi
      .get(id)
      .then((loaded) => {
        latest.current = { title: loaded.title, category: loaded.category ?? '', body: loaded.body };
        setDoc(loaded);
        setTitle(loaded.title);
        setCategory(loaded.category ?? '');
      })
      .catch((cause: Error) => onError(cause.message));
  }, [id, onError]);

  const flush = useCallback(async () => {
    const pending = latest.current;
    if (!pending || saving.current) return;
    saving.current = true;
    setState('saving');
    try {
      await howtoApi.update(id, { title: pending.title.trim() || '제목 없음', category: pending.category.trim() || null, body: pending.body });
      setState(latest.current === pending ? 'saved' : 'dirty');
      onSaved();
    } catch (cause) {
      setState('failed');
      onError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      saving.current = false;
      // 저장하는 사이 더 고쳤으면 그 수정은 `saving`에 막혀 건너뛰어졌다 — 끝난 뒤 이어서 보낸다.
      if (latest.current !== pending && !timer.current) timer.current = setTimeout(() => {
        timer.current = null;
        void flushRef.current();
      }, SAVE_DELAY_MS);
    }
  }, [id, onError, onSaved]);
  const flushRef = useRef(flush);
  flushRef.current = flush;

  const schedule = useCallback(
    (patch: Partial<{ title: string; category: string; body: string }>) => {
      if (!latest.current) return;
      latest.current = { ...latest.current, ...patch };
      setState('dirty');
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        void flush();
      }, SAVE_DELAY_MS);
    },
    [flush],
  );

  // 문서를 닫거나 화면을 떠날 때 못 보낸 수정이 남아 있으면 보낸다.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
        void flush();
      }
    },
    [flush],
  );

  const remove = useCallback(() => {
    if (!window.confirm(`"${title}" 문서를 지운다. 되돌릴 수 없다.`)) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    howtoApi
      .remove(id)
      .then(onRemoved)
      .catch((cause: Error) => onError(cause.message));
  }, [id, onError, onRemoved, title]);

  if (!doc) return <span className="text-[12px] text-muted">불러오는 중…</span>;

  return (
    <div className="mx-auto flex max-w-[820px] flex-col gap-3">
      <div className="flex items-center justify-between gap-3 text-[12px] text-muted">
        <span>{SAVE_LABEL[state]}</span>
        <button type="button" onClick={remove} className="cursor-pointer text-red-700 hover:underline">문서 삭제</button>
      </div>
      <input
        value={title}
        onChange={(event) => {
          setTitle(event.target.value);
          schedule({ title: event.target.value });
        }}
        placeholder="제목 없음"
        maxLength={200}
        className="w-full border-0 bg-transparent text-[32px] font-bold leading-tight outline-none placeholder:text-faint"
      />
      <label className="flex items-center gap-2 text-[12px] text-muted">
        분류
        <input
          value={category}
          list="howto-categories"
          onChange={(event) => {
            setCategory(event.target.value);
            schedule({ category: event.target.value });
          }}
          placeholder="없음"
          maxLength={80}
          className="rounded-md border border-line px-2 py-1 text-[12.5px] text-ink"
        />
        <datalist id="howto-categories">
          {categories.map((name) => <option key={name} value={name} />)}
        </datalist>
      </label>
      <HowtoEditor body={doc.body} onChange={(body) => schedule({ body })} onError={onError} />
    </div>
  );
}
